import { reactive } from 'vue'
import { parseSwingFieldColor } from '@/common/utils/swingFieldColor'

export const FOREGROUND_COLOR_ATTRIBUTE = 'foregroundColor'

const fieldForegroundByKey = reactive<Record<string, string>>({})

function scopedKey(formId: string | number | undefined, fieldName: string): string | undefined {
  const fid = String(formId ?? '').trim()
  if (!fid || fid === '-1') return undefined
  return `${fid}::${fieldName}`
}

function keysFor(formId: string | number | undefined, fieldName: string): string[] {
  const name = String(fieldName ?? '').trim()
  if (!name) return []
  const keys = [name]
  const scoped = scopedKey(formId, name)
  if (scoped) keys.push(scoped)
  return keys
}

/** Swing reload foreground. Scoped by form id, with a bare-name fallback. */
export function readFieldForeground(
  formId: string | number | undefined,
  fieldName: string,
): string | undefined {
  const name = String(fieldName ?? '').trim()
  if (!name) return undefined
  const scoped = scopedKey(formId, name)
  if (scoped && fieldForegroundByKey[scoped]) return fieldForegroundByKey[scoped]
  return fieldForegroundByKey[name]
}

export function rememberFieldForeground(
  formId: string | number | undefined,
  fieldName: string,
  rawColor: unknown,
): void {
  const keys = keysFor(formId, fieldName)
  if (keys.length === 0) return
  const color = parseSwingFieldColor(rawColor)
  if (!color) {
    for (const key of keys) delete fieldForegroundByKey[key]
    return
  }
  for (const key of keys) fieldForegroundByKey[key] = color
}

function absorbAttributeMap(target: Record<string, Record<string, string>>, raw: unknown): void {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return
  for (const [field, attrs] of Object.entries(raw as Record<string, unknown>)) {
    if (!attrs || typeof attrs !== 'object' || Array.isArray(attrs)) continue
    const normalized: Record<string, string> = { ...(target[field] ?? {}) }
    for (const [attr, value] of Object.entries(attrs as Record<string, unknown>)) {
      if (value == null) continue
      normalized[attr] = String(value)
    }
    if (Object.keys(normalized).length > 0) target[field] = normalized
  }
}

/** fieldAttributes from a UniData / changed_data / exchange payload. */
export function collectFieldAttributeMaps(source: unknown): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {}
  const visit = (node: unknown, depth: number) => {
    if (!node || typeof node !== 'object' || Array.isArray(node) || depth > 5) return
    const rec = node as Record<string, unknown>
    absorbAttributeMap(out, rec.fieldAttributes)
    if (rec.fields) visit(rec.fields, depth + 1)
    if (rec.changed_data) visit(rec.changed_data, depth + 1)
    if (rec.jsResult) visit(rec.jsResult, depth + 1)
  }
  visit(source, 0)
  return out
}

export function publishExchangeFieldAttributes(
  source: unknown,
  formId?: string | number,
): void {
  const maps = collectFieldAttributeMaps(source)
  for (const [field, attrs] of Object.entries(maps)) {
    if (!Object.prototype.hasOwnProperty.call(attrs, FOREGROUND_COLOR_ATTRIBUTE)) continue
    rememberFieldForeground(formId, field, attrs[FOREGROUND_COLOR_ATTRIBUTE])
  }
}
